// pages/ContactPage.tsx - FennoFuture contact form
// Sends to PHP backend -> innovation@fennofuture.fi
import React, { useState, useEffect } from 'react';
import {
  Phone, Mail, MapPin, Clock, Send, MessageCircle,
  CheckCircle, AlertCircle } from 'lucide-react';

// Anti-spam configuration
const MIN_FORM_TIME_SECONDS = 5; // Minimum time to fill form (bots are fast)
const RATE_LIMIT_MS = 60000; // 1 minute between submissions

const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: '',
    baseInfo: '',
    website: '' // Honeypot - bots fill this, humans don't see it
  });

  const [formSubmitted, setFormSubmitted] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});

  // Anti-spam state
  const [formStartTime, setFormStartTime] = useState<number>(Date.now());
  const [lastSubmitTime, setLastSubmitTime] = useState<number>(0);

  // Reset form start time when component mounts
  useEffect(() => {
    setFormStartTime(Date.now());
  }, []);

  const validateEmail = (email: string): boolean => {
    if (!email) return true; // Empty is OK (phone can be used instead)
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    setFormData({
      ...formData,
      [name]: value
    });

    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: '' }));
    }

    // Validate email on change
    if (name === 'email' && value && !validateEmail(value)) {
      setFieldErrors(prev => ({ ...prev, email: 'Virheellinen sähköpostiosoite' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    // Anti-spam check 1: Honeypot - if filled, it's a bot
    if (formData.website) {
      // Fake success to not alert the bot
      setFormSubmitted(true);
      setTimeout(() => setFormSubmitted(false), 5000);
      return;
    }

    // Anti-spam check 2: Time-based - form filled too quickly
    const timeSpent = (Date.now() - formStartTime) / 1000;
    if (timeSpent < MIN_FORM_TIME_SECONDS) {
      setSubmitError('Lomake täytettiin liian nopeasti. Odota hetki ja yritä uudelleen.');
      return;
    }

    // Anti-spam check 3: Rate limiting
    if (lastSubmitTime && (Date.now() - lastSubmitTime) < RATE_LIMIT_MS) {
      const waitTime = Math.ceil((RATE_LIMIT_MS - (Date.now() - lastSubmitTime)) / 1000);
      setSubmitError(`Odota ${waitTime} sekuntia ennen seuraavaa lähetystä.`);
      return;
    }

    // Validation: either email OR phone must be provided
    if (!formData.email && !formData.phone) {
      setShowValidation(true);
      return;
    }

    // Validate email format before submitting
    if (formData.email && !validateEmail(formData.email)) {
      setFieldErrors(prev => ({ ...prev, email: 'Virheellinen sähköpostiosoite' }));
      return;
    }

    setShowValidation(false);
    setFieldErrors({});
    setIsSubmitting(true);

    try {
      // Send to PHP backend
      const response = await fetch('/send-mail.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          company: formData.company,
          service: formData.service,
          baseInfo: formData.baseInfo,
          message: formData.message,
          website: formData.website // Honeypot
        }),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to send');
      }

      // Success
      setLastSubmitTime(Date.now());
      setFormSubmitted(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        service: '',
        message: '',
        baseInfo: '',
        website: ''
      });
      setFormStartTime(Date.now());
      setTimeout(() => setFormSubmitted(false), 5000);
    } catch (error) {
      // Show specific error message if available
      const errorMessage = error instanceof Error ? error.message : '';

      // Map server errors to Finnish and highlight the field
      if (errorMessage.includes('Invalid email')) {
        setFieldErrors(prev => ({ ...prev, email: 'Virheellinen sähköpostiosoite' }));
        setSubmitError('Tarkista sähköpostiosoite');
      } else if (errorMessage.includes('Missing required')) {
        setSubmitError('Täytä kaikki pakolliset kentät');
      } else if (errorMessage.includes('Email or phone')) {
        setSubmitError('Anna joko sähköposti tai puhelinnumero');
      } else {
        setSubmitError('Viestin lähetys epäonnistui. Yritä uudelleen tai ota yhteyttä puhelimitse.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactMethods = [
    {
      icon: <Phone className="w-6 h-6" />,
      title: 'Puhelin',
      value: '+358 400 416 466',
      link: 'tel:+358400416466',
      description: 'Ma-Pe 8:00-17:00',
      primary: true
    },
    {
      icon: <Mail className="w-6 h-6" />,
      title: 'Sähköposti',
      value: 'innovation@fennofuture.fi',
      link: 'mailto:innovation@fennofuture.fi',
      description: 'Vastaamme 48h sisällä',
      primary: true
    },
    {
      icon: <MessageCircle className="w-6 h-6" />,
      title: 'WhatsApp',
      value: '+358 400 416 466',
      link: 'https://wa.me/358400416466',
      description: 'Nopea yhteydenotto',
      primary: true
    }
  ];

  return (
    <div className="pt-20 min-h-screen bg-white">
      {/* Hero - matching homepage style */}
      <div className="relative bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900 py-20 overflow-hidden">
        {/* Background elements */}
        <div className="absolute inset-0">
          <div
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage: `linear-gradient(to right, rgba(59, 130, 246, 0.1) 1px, transparent 1px),
                               linear-gradient(to bottom, rgba(59, 130, 246, 0.1) 1px, transparent 1px)`,
              backgroundSize: '50px 50px'
            }}
          />
          <div className="absolute top-10 right-10 w-64 h-64 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full blur-3xl opacity-20" />
          <div className="absolute bottom-10 left-10 w-48 h-48 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full blur-3xl opacity-20" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative text-center text-white">
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            Ota yhteyttä
          </h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            Autamme sinua kaikissa suunnittelu- ja valmistusprojekteissasi.
            Ota rohkeasti yhteyttä - tarjoamme ilmaisen konsultaation!
          </p>
        </div>

        {/* Smooth transition to white */}
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-white via-white/50 to-transparent pointer-events-none" />
      </div>

      {/* Quick Contact Methods */}
      <div className="py-12 -mt-12 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-6">
            {contactMethods.map((method, index) => (
              <a
                key={index}
                href={method.link}
                className={`bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all group ${
                  method.primary ? 'border-2 border-blue-500' : ''
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                    method.primary
                      ? 'bg-gradient-to-br from-blue-500 to-purple-500 text-white'
                      : 'bg-gray-100 text-gray-600 group-hover:bg-gradient-to-br group-hover:from-blue-500 group-hover:to-purple-500 group-hover:text-white'
                  } transition-all`}>
                    {method.icon}
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-gray-900 mb-1">{method.title}</div>
                    <div className="text-blue-600 font-medium mb-1">{method.value}</div>
                    <div className="text-sm text-gray-500">{method.description}</div>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-12">

            {/* Left Column - Contact Info & Map */}
            <div className="lg:col-span-2">
              {/* Office Info */}
              <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">FennoFuture</h3>

                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-blue-600 mt-1" />
                    <div>
                      <p className="font-medium text-gray-900">Käyntiosoite</p>
                      <p className="text-gray-600">Turuntie 3</p>
                      <p className="text-gray-600">37830 Akaa</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Clock className="w-5 h-5 text-blue-600 mt-1" />
                    <div>
                      <p className="font-medium text-gray-900">Aukioloajat</p>
                      <p className="text-gray-600">Ma-Pe: 8:00-17:00</p>
                      <p className="text-gray-600">La-Su: Sopimuksen mukaan</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Map */}
              <div className="bg-white rounded-xl shadow-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-4">Sijainti</h3>
                <div className="rounded-lg overflow-hidden">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1937.6684444444445!2d23.8624!3d61.1667!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x468edf1234567890%3A0x1234567890abcdef!2sTuruntie%203%2C%2037830%20Akaa!5e0!3m2!1sen!2sfi!4v1234567890123!5m2!1sen!2sfi"
                    width="100%"
                    height="300"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="FennoFuture sijainti kartalla"
                  />
                </div>
              </div>
            </div>

            {/* Right Column - Contact Form */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-xl shadow-lg p-8">
                <h2 className="text-3xl font-bold mb-6">Lähetä viesti</h2>

                {showValidation && !formData.email && !formData.phone && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-700">
                      Anna joko sähköposti tai puhelinnumero (tai molemmat)
                    </p>
                  </div>
                )}

                {submitError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-700">{submitError}</p>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Honeypot field - hidden from users, bots will fill it */}
                  <input
                    type="text"
                    name="website"
                    value={formData.website}
                    onChange={handleInputChange}
                    className="absolute -left-[9999px] opacity-0"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                  />

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nimi *
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Nimesi"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Yritys
                      </label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Yrityksen nimi (vapaaehtoinen)"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Sähköposti {!formData.phone && '*'}
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required={!formData.phone}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          fieldErrors.email ? 'border-red-500 bg-red-50' : 'border-gray-300'
                        }`}
                        placeholder="sahkoposti@esimerkki.fi"
                      />
                      {fieldErrors.email && (
                        <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                          <AlertCircle className="w-4 h-4" />
                          {fieldErrors.email}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Puhelin {!formData.email && '*'}
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required={!formData.email}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="+358 40 123 4567"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Palvelutarve *
                    </label>
                    <select
                      name="service"
                      required
                      value={formData.service}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Valitse palvelu...</option>
                      <option value="Tuotteiden suunnittelu">Tuotteiden suunnittelu</option>
                      <option value="Valmistus ja Tuotanto">Valmistus ja Tuotanto</option>
                      <option value="Digitalisointi ja Piirustukset">Digitalisointi ja Piirustukset</option>
                      <option value="Korjaus ja Huolto">Korjaus ja Huolto</option>
                      <option value="Muu">Muu</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Pohjatiedot
                    </label>
                    <textarea
                      name="baseInfo"
                      rows={3}
                      value={formData.baseInfo}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Kirjoita kuvaus tähän"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Viesti *
                    </label>
                    <textarea
                      name="message"
                      required
                      rows={6}
                      value={formData.message}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Kerro projektistasi tai tarpeestasi..."
                    />
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Vinkki!</h4>
                    <ul className="space-y-1 text-sm text-gray-700">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                        <span>Ota kuvia aiheesta</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                        <span>Anna mittakaava</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                        <span>Anna käyttötarkoitus</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                        <span>Mainitse toivottu aikataulu</span>
                      </li>
                    </ul>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-full px-8 py-4 rounded-full font-semibold transition-all flex items-center justify-center gap-2 ${
                      isSubmitting
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:shadow-xl'
                    }`}
                  >
                    <Send className="w-5 h-5" />
                    {isSubmitting ? 'Lähetetään...' : 'Lähetä viesti'}
                  </button>

                  {formSubmitted && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-green-700">
                        Viesti lähetetty! Vastaamme mahdollisimman pian.
                      </p>
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
